import { Link, useNavigate } from 'react-router-dom';
import type { Task, Client, Project } from '@/types';
import { isTaskOverdue } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Calendar,
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
  Building2,
  FolderOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface TaskCardProps {
  task: Task;
  clientName?: string;
  projectName?: string;
}

const priorityConfig = {
  high: {
    label: 'High',
    className: 'border-red-500 text-red-500',
    borderColor: 'border-l-red-500',
  },
  medium: {
    label: 'Medium',
    className: 'border-muted-foreground text-muted-foreground',
    borderColor: 'border-l-muted-foreground',
  },
  low: {
    label: 'Low',
    className: 'border-green-600 text-green-600',
    borderColor: 'border-l-green-600',
  },
};

const statusConfig = {
  'not-started': {
    label: 'Not Started',
    icon: Circle,
    className: 'text-muted-foreground',
  },
  'in-progress': {
    label: 'In Progress',
    icon: Clock,
    className: 'text-blue-600 dark:text-blue-400',
  },
  'completed': {
    label: 'Completed',
    icon: CheckCircle2,
    className: 'text-green-600 dark:text-green-400',
  },
};

export function TaskCard({ task, clientName, projectName }: TaskCardProps) {
  const navigate = useNavigate();
  const priorityConf = priorityConfig[task.priority];
  const statusConf = statusConfig[task.status];
  const StatusIcon = statusConf.icon;
  const overdue = isTaskOverdue(task);

  return (
    <Card
      className={cn(
        'border border-border border-l-4 hover:bg-muted/50 transition-colors cursor-pointer group',
        priorityConf.borderColor
      )}
      onClick={() => navigate(`/tasks/${task.id}`)}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <Link
              to={`/tasks/${task.id}`}
              className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2"
              onClick={(e) => e.stopPropagation()}
            >
              {task.title}
            </Link>
          </div>
          <Badge className={priorityConf.className}>
            {priorityConf.label}
          </Badge>
        </div>

        {/* Description */}
        {task.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {task.description}
          </p>
        )}

        {/* Client & Project */}
        {(clientName || projectName) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {clientName && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 dark:bg-muted/20 px-2 py-1 rounded">
                <Building2 className="h-3 w-3" />
                <span>{clientName}</span>
              </div>
            )}
            {projectName && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 dark:bg-muted/20 px-2 py-1 rounded">
                <FolderOpen className="h-3 w-3" />
                <span>{projectName}</span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-border/50">
          {/* Status */}
          <div className="flex items-center gap-1.5 text-xs">
            <StatusIcon className={cn('h-3.5 w-3.5', statusConf.className)} />
            <span className={statusConf.className}>{statusConf.label}</span>
          </div>

          {/* Deadline */}
          {task.deadline ? (
            <div
              className={cn(
                'flex items-center gap-1.5 text-xs',
                overdue ? 'text-destructive font-medium' : 'text-muted-foreground'
              )}
            >
              {overdue && <AlertCircle className="h-3.5 w-3.5" />}
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {new Date(task.deadline).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">No deadline</span>
          )}
        </div>

        {/* Quick Actions (appear on hover) */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-card via-card to-transparent p-4 translate-y-full group-hover:translate-y-0 transition-transform">
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/tasks/${task.id}`);
              }}
            >
              View Details
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/tasks/${task.id}/edit`);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface TasksCardGridProps {
  tasks: Task[];
  clients: Client[];
  projects: Project[];
  emptyState?: React.ReactNode;
}

export function TasksCardGrid({
  tasks,
  clients,
  projects,
  emptyState,
}: TasksCardGridProps) {
  if (tasks.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  const getClientName = (clientId?: string) => {
    if (!clientId) return undefined;
    return clients.find((c) => c.id === clientId)?.name;
  };

  const getProjectName = (projectId?: string) => {
    if (!projectId) return undefined;
    return projects.find((p) => p.id === projectId)?.name;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {tasks.map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          clientName={getClientName(task.clientId)}
          projectName={getProjectName(task.projectId)}
        />
      ))}
    </div>
  );
}
